import { createTheme, type Direction, type PaletteMode, type Theme } from '@mui/material';
import { buildComponents } from './components';
import { buildPalette } from './palette';
import { buildTypography } from './typography';
import { layout, radii, softShadows, spacingUnit } from './tokens';

declare module '@mui/material/styles' {
  interface Theme {
    tokens: { radii: typeof radii; shadows: typeof softShadows };
  }
  interface ThemeOptions {
    tokens?: { radii: typeof radii; shadows: typeof softShadows };
  }
}

/** MUI needs 25 shadow slots; we map our five steps onto them. */
const shadowScale: string[] = [
  'none',
  softShadows.xs,
  softShadows.xs,
  softShadows.sm,
  softShadows.sm,
  softShadows.md,
  softShadows.md,
  softShadows.popover,
  softShadows.popover,
  ...Array<string>(16).fill(softShadows.overlay),
];

/**
 * Nothing moves (owner, 2026-10-21). Every MUI transition — a menu's grow, a
 * drawer's slide, a dialog's fade, a collapse, a tooltip, a hover settle — is
 * built from `theme.transitions`, so zeroing it here is what makes them all
 * appear in their final state on the first frame. `create` still names each
 * property, with no duration, so a component that joins two of them (Grow
 * writes `opacity, transform`) gets a valid declaration. `getAutoHeightDuration`
 * is what Grow and Collapse ask for with `timeout="auto"`; it ignores every
 * duration below, so it is zeroed on its own. The baseline adds a CSS
 * backstop for transitions written outside the theme (notistack's own).
 */
const STILL = {
  duration: {
    shortest: 0,
    shorter: 0,
    short: 0,
    standard: 0,
    complex: 0,
    enteringScreen: 0,
    leavingScreen: 0,
  },
  create: (props: string | string[] = ['all']) =>
    (Array.isArray(props) ? props : [props]).map((prop) => `${prop} 0ms`).join(','),
  getAutoHeightDuration: () => 0,
};

export function createAppTheme(mode: PaletteMode, direction: Direction): Theme {
  const theme = createTheme({
    direction,
    palette: buildPalette(mode),
    typography: buildTypography(direction === 'rtl' ? 'rtl' : 'ltr'),
    shape: { borderRadius: radii.sm },
    spacing: spacingUnit,
    shadows: shadowScale as Theme['shadows'],
    /*
     * MUI's toolbar mixin is responsive (56/48/64px) and is emitted after any
     * `sx`, so a Toolbar cannot be sized from the outside. The app bar and the
     * sidebar's brand block share `layout.topbarHeight`; this is where the
     * Toolbar learns it, border included, so their bottom rules form one line.
     */
    mixins: { toolbar: { minHeight: layout.topbarHeight - 1 } },
    transitions: STILL,
    components: buildComponents(),
    tokens: { radii, shadows: softShadows },
  });
  // The scale is already tight; letting MUI shrink it further hurts legibility.
  return theme;
}

export * from './tokens';
